/**
 * AuthProvider is the single source of truth for authentication state.
 *
 * Responsibilities:
 * - Stores and exposes the current auth token (persisted in localStorage).
 * - Fetches and caches the current authenticated user via React Query.
 * - Exposes the session's `mfa_required` flag, sourced from `GET /session` and
 *   kept in the React Query cache (never in localStorage). The session query
 *   is the single source of truth — it never depends on `user.mfa_enabled`.
 * - Automatically clears auth + user state on invalid token (401/403).
 * - Keeps auth state in sync across browser tabs using the `storage` event.
 *
 * Behavior:
 * - On mount, reads the token from localStorage.
 * - If a token exists, fires both `authUser` and `session` queries. Until
 *   both resolve, `isLoading` is `true` and AppGate blocks rendering — this
 *   prevents MFA-required content from ever being shown before the gate
 *   decision is made.
 * - `setSession(accessToken)` is used after login / unlock-confirm: it
 *   rotates the token, which triggers a fresh `/session` fetch under the
 *   new token key. AccessToken from those endpoints does not carry
 *   `mfa_required`, so the cache is intentionally not primed.
 * - `updateSession(session)` is used after MFA verify / MFA enroll, which
 *   return a fresh `CurrentSession` but do NOT rotate the token. It primes
 *   the session cache directly so AppGate reacts without a network round-trip.
 * - If the token is removed/changed (login/logout/impersonation), clears the
 *   cached user, session, and mfaStatus entries.
 * - `refetchUser()` can be used to manually refresh the user data
 *   without forcing a full app reload.
 *
 * Notes:
 * - React Query keys include `token` to ensure session-scoped data does not
 *   leak across auth contexts on token rotation.
 * - The provider intentionally exposes derived loading/error flags (combining
 *   the user and session queries) so consumers do not need to access React
 *   Query internals directly.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type {
  AccessToken,
  CurrentSession,
  CurrentSessionResponse,
  User,
  UserResponse,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { Authorizations } from "~/api/types";
import { useSetUserConfig } from "~/featureFlags/provider";
import {
  AUTH_TOKEN_STORAGE_KEY,
  SESSION_UPDATED_AT_STORAGE_KEY,
} from "~/helpers/consts";
import { tryCatch } from "~/helpers/tryCatch";
import { SsoStatus, useSso } from "~/hooks/useSso";
import { getWhiteLabelConfig } from "~/utils/white-label/getWhiteLabelConfig";

type ExtendedUserResponse = UserResponse & {
  authorizations?: Authorizations;
};

type AuthQueryResult = {
  user: User | null;
  authorizations: Authorizations | null;
} | null;

type AuthContextValue = {
  token: string | null;
  user: User | null;
  authorizations: Authorizations | null;

  mfaRequired: boolean;

  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;

  ssoStatus: SsoStatus;

  refetchUser: () => Promise<User | null>;
  setToken: (token: string | null) => void;
  setSession: (accessToken: AccessToken) => void;
  updateSession: (session: CurrentSession) => void;
  clearAuth: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function readToken() {
  if (typeof window === "undefined") return null;

  return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
}

function useSessionQuery(token: string | null, onInvalidToken: () => void) {
  return useQuery<CurrentSessionResponse | null>({
    queryKey: queryKeys.session(token),
    // Always fetch the session whenever a token exists. The session is the
    // sole authority on `mfa_required` — we never derive it from user fields
    // (which can be stale or schema-drifted). On login / unlock the token
    // rotates, the query key changes, and this query refires automatically.
    // MFA verify / enroll prime the cache via `updateSession` instead.
    enabled: !!token,
    queryFn: async () => {
      const result = await tryCatch(apiClient.session.sessionList());

      if (result.error) {
        const statusCode = result.error.status;

        if (statusCode === 401 || statusCode === 403) {
          onInvalidToken();

          return null;
        }

        throw result.error;
      }

      return result.data.data;
    },
    // No global window-focus refetch — every tab on every page would otherwise
    // ping `/session` on each refocus. Cross-tab session-state propagation is
    // handled via the `SESSION_UPDATED_AT_STORAGE_KEY` storage-event marker
    // (see the storage listener below): a verify / enroll in any tab fires
    // a one-way broadcast that invalidates this query in every other tab.
    refetchOnWindowFocus: false,
    retry: 1,
    // Within a single focused tab, session state only changes via login /
    // verify / enroll / unlock — all of which either rotate the token
    // (refiring this query under a new key) or call `updateSession` to prime
    // the cache directly. Treat data as fresh forever; `clearUserCache`
    // handles explicit resets and the storage-event marker covers cross-tab
    // drift.
    staleTime: Infinity,
  });
}

function useAuthUserQuery(token: string | null, onInvalidToken: () => void) {
  return useQuery<AuthQueryResult>({
    queryKey: queryKeys.authUser(token),
    enabled: !!token,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.user.userList({
          headers: { "Authorize-Member-Actions": "update" },
        }),
      );

      if (result.error) {
        const statusCode = result.error.status;

        if (statusCode === 401 || statusCode === 403) {
          onInvalidToken();

          return null;
        }

        throw result.error;
      }

      const payload = result.data?.data as ExtendedUserResponse | undefined;

      return {
        user: payload?.user ?? null,
        authorizations: payload?.authorizations ?? null,
      };
    },
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState<string | null>(() => readToken());
  const setUserConfig = useSetUserConfig();
  const { alliaiapp } = getWhiteLabelConfig();

  const clearUserCache = useCallback(() => {
    // Every cached query is scoped to the authenticated user one way or
    // another (auth/session/mfa, teams, subscription, sites, ...), so wipe
    // the whole cache on logout / token rotation. Removing only the
    // auth-scoped keys let the previous user's data leak into the next
    // session — e.g. after logout → sign-up the team select showed the old
    // user's teams, because `useTeams` (`refetchOnMount: false`) kept
    // serving the stale `["teams"]` entry forever.
    queryClient.clear();
  }, [queryClient]);

  const handleInvalidToken = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    setTokenState(null);
    clearUserCache();
  }, [clearUserCache]);

  // Sync auth state across browser tabs.
  // The `storage` event fires only in other tabs when localStorage changes.
  // This ensures that login/logout/impersonation in one tab
  // correctly resets auth state in all open tabs.
  //
  // `SESSION_UPDATED_AT_STORAGE_KEY` covers the cross-tab case where the
  // token does NOT rotate (MFA verify / enroll): the in-tab `updateSession`
  // bumps the timestamp, and every other tab with the same token invalidates
  // session + authUser so a tab parked on /verify-mfa picks up the new
  // `mfa_required: false` state without polling or focus heuristics.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === AUTH_TOKEN_STORAGE_KEY) {
        setTokenState(e.newValue);
        clearUserCache();

        return;
      }
      if (e.key === SESSION_UPDATED_AT_STORAGE_KEY && e.newValue !== null) {
        queryClient.invalidateQueries({ queryKey: queryKeys.sessionRoot() });
        queryClient.invalidateQueries({ queryKey: queryKeys.authUserRoot() });
      }
    };
    window.addEventListener("storage", onStorage);

    return () => window.removeEventListener("storage", onStorage);
  }, [clearUserCache, queryClient]);

  const setToken = useCallback(
    (next: string | null) => {
      if (!next) localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      else localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, next);

      setTokenState(next);
      clearUserCache();
    },
    [clearUserCache],
  );

  const clearAuth = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    setTokenState(null);
    clearUserCache();
  }, [clearUserCache]);

  const { status: ssoStatus } = useSso({
    alliaiapp,
    token,
    setToken,
  });

  const {
    data,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    isError: isUserError,
    error: userError,
    refetch,
  } = useAuthUserQuery(token, handleInvalidToken);

  const user = data?.user ?? null;
  const authorizations = data?.authorizations ?? null;

  const sessionQuery = useSessionQuery(token, handleInvalidToken);
  // Fail-safe default: while the session is unresolved we assume MFA IS
  // required. AppGate's `isLoading`-driven block hides the UI during this
  // window anyway, but any consumer that reads `mfaRequired` directly
  // (e.g. TeamProvider's `enabled` gate) must not race ahead and fire
  // protected requests under a session that may yet turn out to be
  // `mfa_required: true`. Once the query resolves, the server is the sole
  // source of truth for the flag (no inference from `user`).
  const session = sessionQuery.data?.current_session ?? null;
  const mfaRequired = session ? session.mfa_required : true;

  // Used after login / unlock-confirm: rotates the token. `setToken` clears
  // the previous session cache; the new `/session` query fires automatically
  // under the new token key. We intentionally do NOT prime the session cache
  // here — `AccessToken` from those endpoints does not carry `mfa_required`,
  // so the only way to learn it is the server round-trip.
  const setSession = useCallback(
    (next: AccessToken) => {
      setToken(next.key);
    },
    [setToken],
  );

  // Used after MFA verify / enroll: those endpoints return a fresh
  // `CurrentSession` but keep the token unchanged. Priming the cache lets
  // AppGate flip `mfaRequired` to `false` without a follow-up `/session`
  // round-trip. No-op without a token (defensive — callers always have one).
  const updateSession = useCallback(
    (next: CurrentSession) => {
      if (!token) return;
      queryClient.setQueryData<CurrentSessionResponse>(
        queryKeys.session(token),
        { current_session: next },
      );
      // The backend scopes `authorizations` to the session's MFA state:
      // while `mfa_required: true` it returns an empty set so a mid-MFA
      // user can't, e.g., change password from a half-authenticated state.
      // Once verify / enroll flips `mfa_required` to false, the user query
      // would normally not refetch (token-keyed, token unchanged) and the
      // stale empty-set would silently disable every permission-gated
      // control downstream. Invalidate to pull the now-permitted scope.
      queryClient.invalidateQueries({ queryKey: queryKeys.authUser(token) });
      // Notify other tabs holding the same token that the session changed.
      // The `storage` event fires only in OTHER tabs, so this is a one-way
      // broadcast — no echo, no listener cycle. Wrapped because Safari
      // private mode / quota-exhausted contexts can throw on `setItem`; the
      // local tab already has the fresh state, so a failed broadcast is
      // recoverable (the parked tab will pick it up on its next normal
      // refetch trigger).
      try {
        localStorage.setItem(
          SESSION_UPDATED_AT_STORAGE_KEY,
          String(Date.now()),
        );
      } catch {
        /* noop — see comment above */
      }
    },
    [token, queryClient],
  );

  // `isLoading` semantically means "auth state is still being resolved": both
  // the user identity AND the session must be known before AppGate can decide
  // what to render. Without this combination, a route can flash visible
  // content while the session query is still in flight.
  const isLoading = isUserLoading || sessionQuery.isLoading;
  const isFetching = isUserFetching || sessionQuery.isFetching;
  // `isError` surfaces failures from either query so AppGate can present a
  // recoverable error screen (with Sign Out) instead of a forever-spinner
  // when the network/server is down. 401/403 are handled inline via
  // `handleInvalidToken` and never reach this flag.
  const isError = isUserError || sessionQuery.isError;
  const error = userError ?? sessionQuery.error ?? null;

  /**
   * Sync user-based feature restrictions into the FeatureFlagsProvider.*
   * Once the user is fetched, we apply `user.features` as additional restrictions.
   */
  useEffect(() => {
    if (!token) {
      setUserConfig(null);

      return;
    }

    if (user) {
      setUserConfig(user.features);

      return;
    }
  }, [token, user, setUserConfig]);

  const refetchUser = useCallback(async () => {
    const res = await refetch();

    return res.data?.user ?? null;
  }, [refetch]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user: token ? (user ?? null) : null,
      authorizations: token ? authorizations : null,

      mfaRequired: token ? mfaRequired : false,

      isLoading: token ? isLoading : false,
      isFetching: token ? isFetching : false,
      isError: token ? isError : false,
      error: token ? (error ?? null) : null,
      ssoStatus,
      refetchUser,
      setToken,
      setSession,
      updateSession,
      clearAuth,
    }),
    [
      token,
      user,
      authorizations,
      mfaRequired,
      isLoading,
      isFetching,
      isError,
      error,
      ssoStatus,
      refetchUser,
      setToken,
      setSession,
      updateSession,
      clearAuth,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");

  return ctx;
}
